import React from 'react';

// Base skeleton component with shimmer animation
export const Skeleton = ({ className = '', style = {} }) => (
  <div
    className={`bg-slate-200 rounded animate-pulse ${className}`}
    style={style}
  />
);

// Skeleton for project cards in the dashboard
export const ProjectCardSkeleton = () => (
  <div className="bg-white rounded-[24px] border border-slate-100 shadow-sm flex flex-col overflow-hidden">
    <div className="p-3 pb-0">
      <div className="relative aspect-[16/10] bg-slate-100 rounded-2xl overflow-hidden animate-pulse">
        {/* Duration badge skeleton */}
        <div className="absolute bottom-3 end-3 w-12 h-5 bg-slate-200 rounded-lg" />
      </div>
    </div>
    <div className="p-4 flex-1 flex flex-col">
      <div className="flex justify-between items-start mb-3">
        {/* Title skeleton */}
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-slate-200 rounded-lg w-3/4 animate-pulse" />
          <div className="h-4 bg-slate-200 rounded-lg w-1/2 animate-pulse" />
        </div>
      </div>
      <div className="mt-auto flex items-center justify-between">
        {/* Date skeleton */}
        <div className="h-3 bg-slate-200 rounded w-16 animate-pulse" />
        {/* Status badge skeleton */}
        <div className="h-4 bg-slate-100 rounded-md w-14 animate-pulse" />
      </div>
    </div>
  </div>
);

// Skeleton grid for dashboard loading
export const ProjectGridSkeleton = ({ count = 8 }) => (
  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <ProjectCardSkeleton key={i} />
    ))}
  </div>
);

// Editor content skeleton (just the content area, no outer wrapper)
export const EditorContentSkeleton = () => (
  <div className="flex-1 flex items-center justify-center relative z-10">
    <div className="w-full max-w-4xl mx-auto px-4 md:px-12 pt-10">
      {/* Caption blocks skeleton */}
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <div className="flex items-start gap-4">
              {/* Timestamp skeleton */}
              <div className="w-16 h-6 bg-slate-100 rounded-lg animate-pulse shrink-0" />
              {/* Text content skeleton */}
              <div className="flex-1 space-y-3">
                <div className="h-5 bg-slate-200 rounded-lg w-full animate-pulse" />
                <div className="h-5 bg-slate-200 rounded-lg w-4/5 animate-pulse" />
                <div className="h-5 bg-slate-100 rounded-lg w-2/3 animate-pulse" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Editor skeleton showing the full editor layout
export const EditorSkeleton = () => (
  <div className="flex flex-col h-screen font-sans overflow-hidden relative bg-[#f8fafc]">
    {/* Background Grid Pattern */}
    <div 
      className="absolute inset-0 z-0 pointer-events-none opacity-40"
      style={{ 
        backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)', 
        backgroundSize: '24px 24px' 
      }} 
    />

    {/* Header skeleton */}
    <header className="h-16 px-6 lg:px-10 bg-white/80 backdrop-blur-md border-b border-slate-100 flex items-center justify-between shrink-0 z-30 relative">
      {/* Left: Back + Project Name */}
      <div className="flex items-center gap-6 w-1/3">
        <div className="w-10 h-10 rounded-full bg-slate-100 animate-pulse" />
        <div className="flex flex-col gap-2">
          <div className="h-4 bg-slate-200 rounded-lg w-40 animate-pulse" />
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-slate-200 animate-pulse" />
            <div className="h-2 bg-slate-200 rounded w-12 animate-pulse" />
          </div>
        </div>
      </div>

      {/* Center: View Mode Toggle skeleton */}
      <div className="flex items-center justify-center w-1/3">
        <div className="flex items-center bg-slate-100/80 rounded-full p-1 border border-slate-100">
          <div className="w-20 h-8 rounded-full bg-slate-200 animate-pulse" />
          <div className="w-20 h-8 rounded-full bg-slate-100 animate-pulse" />
        </div>
      </div>

      {/* Right: Controls skeleton */}
      <div className="flex items-center justify-end gap-3 w-1/3">
        <div className="flex items-center bg-slate-100/80 rounded-full p-1">
          <div className="w-8 h-8 rounded-full bg-slate-200 animate-pulse" />
          <div className="w-8 h-8 rounded-full bg-slate-200 animate-pulse" />
        </div>
        <div className="w-10 h-10 rounded-full bg-slate-100 animate-pulse" />
        <div className="w-24 h-10 rounded-full bg-slate-200 animate-pulse" />
      </div>
    </header>

    {/* Main Content Area */}
    <div className="flex-1 flex overflow-hidden relative z-10">
      {/* Editor Content skeleton */}
      <div className="flex-1 flex flex-col relative min-w-0">
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto px-4 md:px-12 pt-10 pb-48 max-w-4xl">
            {/* Caption blocks skeleton */}
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                  <div className="flex items-start gap-4">
                    {/* Timestamp skeleton */}
                    <div className="w-16 h-6 bg-slate-100 rounded-lg animate-pulse shrink-0" />
                    {/* Text content skeleton */}
                    <div className="flex-1 space-y-3">
                      <div className="h-5 bg-slate-200 rounded-lg w-full animate-pulse" />
                      <div className="h-5 bg-slate-200 rounded-lg w-4/5 animate-pulse" />
                      <div className="h-5 bg-slate-100 rounded-lg w-2/3 animate-pulse" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Floating Audio Player skeleton */}
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-100 px-4 py-3 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-slate-100 animate-pulse" />
            <div className="w-48 h-2 bg-slate-200 rounded-full animate-pulse" />
            <div className="w-16 h-4 bg-slate-100 rounded animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// App shell skeleton for initial auth loading
export const AppShellSkeleton = () => (
  <div className="min-h-screen bg-[#f8fafc]">
    {/* Header skeleton */}
    <div className="h-16 bg-white border-b border-slate-100 px-6 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-slate-200 animate-pulse" />
        <div className="h-5 w-24 bg-slate-200 rounded-lg animate-pulse" />
      </div>
      <div className="flex items-center gap-3">
        <div className="w-20 h-8 bg-slate-100 rounded-lg animate-pulse" />
        <div className="w-10 h-10 rounded-full bg-slate-200 animate-pulse" />
      </div>
    </div>
    
    {/* Content skeleton */}
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Announcement skeleton */}
      <div className="h-24 bg-white rounded-3xl border border-slate-100 mb-6 animate-pulse" />
      
      {/* Search bar skeleton */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 h-12 bg-white rounded-2xl animate-pulse" />
        <div className="w-36 h-12 bg-slate-200 rounded-2xl animate-pulse" />
      </div>
      
      {/* Title skeleton */}
      <div className="h-7 w-48 bg-slate-200 rounded-lg mb-6 animate-pulse" />
      
      {/* Grid skeleton */}
      <ProjectGridSkeleton count={8} />
    </div>
  </div>
);

// Dashboard content skeleton (without the header/sidebar - just the main content)
export const DashboardContentSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between px-2">
      <div className="h-7 w-48 bg-slate-200 rounded-lg animate-pulse" />
      <div className="h-5 w-24 bg-slate-100 rounded-lg animate-pulse" />
    </div>
    <ProjectGridSkeleton count={8} />
  </div>
);

export default Skeleton;
